import React, { useEffect, useMemo, useState } from 'react';

import { RiAddLine, RiEditLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Field, FieldDescription } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { LocationPreview } from '@/design-system/location-preview';
import {
  PlacesAutocomplete,
  PlacePick,
} from '@/design-system/places-autocomplete';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';
import {
  CreateHubPayload,
  Hub,
  HUB_STATUS_LABELS,
  HubStatus,
  hubsAPI,
  UpdateHubPayload,
} from '@/services/api/hubs';


interface HubFormModalProps {
  isOpen: boolean;
  /**
   * Null = create mode. A hub = edit mode (code is read-only).
   */
  hub: Hub | null;
  onClose: () => void;
  onSaved: (hub: Hub) => void;
}

interface FormState {
  code: string;
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  clockInRadius: string;
  phoneNumber: string;
  status: HubStatus;
}

const EMPTY: FormState = {
  code: '',
  name: '',
  address: '',
  latitude: '',
  longitude: '',
  clockInRadius: '5000',
  phoneNumber: '',
  status: 'active',
};

const HubFormModal: React.FC<HubFormModalProps> = ({
  isOpen,
  hub,
  onClose,
  onSaved,
}) => {
  const isEdit = hub !== null;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    setIsSubmitting(false);
    setFieldErrors({});
    if (hub) {
      setForm({
        code: hub.code,
        name: hub.name,
        address: hub.address ?? '',
        latitude: String(hub.latitude),
        longitude: String(hub.longitude),
        clockInRadius: String(hub.clockInRadius ?? 5000),
        phoneNumber: hub.phoneNumber ?? '',
        status: hub.status,
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, hub]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const parsedLat = useMemo(() => {
    const n = Number(form.latitude);
    return Number.isFinite(n) ? n : null;
  }, [form.latitude]);

  const parsedLng = useMemo(() => {
    const n = Number(form.longitude);
    return Number.isFinite(n) ? n : null;
  }, [form.longitude]);

  const parsedRadius = useMemo(() => {
    const n = Number(form.clockInRadius);
    return Number.isFinite(n) ? n : null;
  }, [form.clockInRadius]);

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};

    if (!isEdit && !form.code.trim()) {
      errors.code = 'Code is required';
    }

    if (!form.name.trim()) {
      errors.name = 'Name is required';
    } else if (form.name.trim().length > 50) {
      errors.name = 'Name must be 50 characters or less';
    }

    if (!form.address.trim()) {
      errors.address = 'Address is required';
    }

    if (parsedLat === null || form.latitude.trim() === '') {
      errors.latitude = 'Latitude is required';
    } else if (parsedLat < -90 || parsedLat > 90) {
      errors.latitude = 'Latitude must be between -90 and 90';
    }

    if (parsedLng === null || form.longitude.trim() === '') {
      errors.longitude = 'Longitude is required';
    } else if (parsedLng < -180 || parsedLng > 180) {
      errors.longitude = 'Longitude must be between -180 and 180';
    }

    if (parsedRadius === null || parsedRadius <= 500) {
      errors.clockInRadius = 'Radius must be greater than 500m';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (
      !validate() ||
      parsedLat === null ||
      parsedLng === null ||
      parsedRadius === null
    )
      return;

    setIsSubmitting(true);
    try {
      let saved: Hub;
      const phone = form.phoneNumber.trim();
      if (isEdit && hub) {
        const payload: UpdateHubPayload = {
          name: form.name.trim(),
          address: form.address.trim(),
          latitude: parsedLat,
          longitude: parsedLng,
          clockInRadius: parsedRadius,
          coverageRadius: parsedRadius,
          phoneNumber: phone ? phone : null,
          status: form.status,
        };
        saved = await hubsAPI.update(hub.id, payload);
        toast.success('Hub updated', { description: `${saved.code} saved.` });
      } else {
        const payload: CreateHubPayload = {
          code: form.code.trim(),
          name: form.name.trim(),
          address: form.address.trim(),
          latitude: parsedLat,
          longitude: parsedLng,
          clockInRadius: parsedRadius,
          coverageRadius: parsedRadius,
          ...(phone ? { phoneNumber: phone } : {}),
        };
        saved = await hubsAPI.create(payload);
        toast.success('Hub created', { description: `${saved.code} added.` });
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(isEdit ? 'Could not update hub' : 'Could not create hub', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const handlePlacePick = (place: PlacePick) => {
    const lat = Number(place.latitude.toFixed(6));
    const lng = Number(place.longitude.toFixed(6));
    setForm((prev) => ({
      ...prev,
      address: place.address,
      latitude: String(lat),
      longitude: String(lng),
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.address;
      delete next.latitude;
      delete next.longitude;
      return next;
    });
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={isEdit ? <RiEditLine /> : <RiAddLine />}
      iconTone="primary"
      title={isEdit ? 'Edit hub' : 'New hub'}
      subtitle={
        isEdit && hub
          ? `${hub.code} · ${hub.name}`
          : 'Defines a physical Dash facility. Zones live under hubs.'
      }
      size="lg"
      confirmOnClose
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting
              ? isEdit
                ? 'Saving…'
                : 'Creating…'
              : isEdit
                ? 'Save changes'
                : 'Create hub'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field>
          <Label htmlFor="hub-code" required={!isEdit}>
            Code
            {isEdit && (
              <Badge status="neutral" size="sm" className="ml-1">
                Immutable
              </Badge>
            )}
          </Label>
          <InputRoot size="md" invalid={!!fieldErrors.code}>
            <Input
              id="hub-code"
              type="text"
              value={form.code}
              disabled={isEdit}
              onChange={(e) => setField('code', e.target.value.toUpperCase())}
              placeholder="DASH_JKT_UTARA"
              className="font-mono"
            />
          </InputRoot>
          {fieldErrors.code && <Hint tone="error">{fieldErrors.code}</Hint>}
        </Field>

        <Field>
          <Label htmlFor="hub-name" required>
            Name
          </Label>
          <InputRoot size="md" invalid={!!fieldErrors.name}>
            <Input
              id="hub-name"
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="DASH-JKT-UTARA"
              maxLength={50}
            />
          </InputRoot>
          {fieldErrors.name && <Hint tone="error">{fieldErrors.name}</Hint>}
        </Field>

        <Field>
          <Label
            htmlFor="hub-address"
            required
            hint="Pick a suggestion to auto-fill coordinates"
          >
            Address
          </Label>
          <PlacesAutocomplete
            inputId="hub-address"
            value={form.address}
            onChange={(v) => setField('address', v)}
            onPick={handlePlacePick}
            placeholder="Jl. Boulevard Raya, Kelapa Gading, Jakarta Utara"
          />
          {fieldErrors.address && (
            <Hint tone="error">{fieldErrors.address}</Hint>
          )}
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label htmlFor="hub-latitude" required>
              Latitude
            </Label>
            <InputRoot size="md" invalid={!!fieldErrors.latitude}>
              <Input
                id="hub-latitude"
                type="number"
                step="0.000001"
                min={-90}
                max={90}
                value={form.latitude}
                onChange={(e) => setField('latitude', e.target.value)}
                placeholder="-6.158472"
                className="font-mono"
              />
            </InputRoot>
            {fieldErrors.latitude && (
              <Hint tone="error">{fieldErrors.latitude}</Hint>
            )}
          </Field>

          <Field>
            <Label htmlFor="hub-longitude" required>
              Longitude
            </Label>
            <InputRoot size="md" invalid={!!fieldErrors.longitude}>
              <Input
                id="hub-longitude"
                type="number"
                step="0.000001"
                min={-180}
                max={180}
                value={form.longitude}
                onChange={(e) => setField('longitude', e.target.value)}
                placeholder="106.910556"
                className="font-mono"
              />
            </InputRoot>
            {fieldErrors.longitude && (
              <Hint tone="error">{fieldErrors.longitude}</Hint>
            )}
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label htmlFor="hub-radius" required>
              Clock-in radius (m)
            </Label>
            <InputRoot size="md" invalid={!!fieldErrors.clockInRadius}>
              <Input
                id="hub-radius"
                type="number"
                min={501}
                step="1"
                value={form.clockInRadius}
                onChange={(e) => setField('clockInRadius', e.target.value)}
                placeholder="5000"
                className="font-mono"
              />
            </InputRoot>
            {fieldErrors.clockInRadius && (
              <Hint tone="error">{fieldErrors.clockInRadius}</Hint>
            )}
          </Field>

          <Field>
            <Label htmlFor="hub-phone">Phone number</Label>
            <InputRoot size="md">
              <Input
                id="hub-phone"
                type="tel"
                value={form.phoneNumber}
                onChange={(e) => setField('phoneNumber', e.target.value)}
                placeholder="6281234567890"
                className="font-mono"
              />
            </InputRoot>
          </Field>
        </div>

        <Field>
          <div className="flex items-center justify-between">
            <Label>Map preview</Label>
            {parsedLat !== null && parsedLng !== null && (
              <a
                href={`https://www.google.com/maps?q=${parsedLat},${parsedLng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[11px] tabular-nums text-accent hover:underline"
              >
                {parsedLat.toFixed(6)}, {parsedLng.toFixed(6)}
                <i className="ri-external-link-line ml-1 text-[12px]" />
              </a>
            )}
          </div>
          <LocationPreview latitude={parsedLat} longitude={parsedLng} />
        </Field>

        {isEdit && (
          <Field>
            <Label>Status</Label>
            <SegmentedControl
              value={form.status}
              onValueChange={(v: string) => {
                if (v) setField('status', v as HubStatus);
              }}
              className="w-full [&>*]:flex-1"
            >
              {(['active', 'inactive'] as HubStatus[]).map((opt) => (
                <SegmentedItem key={opt} value={opt}>
                  {HUB_STATUS_LABELS[opt]}
                </SegmentedItem>
              ))}
            </SegmentedControl>
            <FieldDescription>
              Pitstop has no status — this control is a no-op until the
              upstream service supports it.
            </FieldDescription>
          </Field>
        )}
      </div>
    </FormModal>
  );
};

export default HubFormModal;
